import React from 'react'
import { motion } from 'framer-motion'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowLeft, 
  BarChart3, 
  TrendingUp, 
  Users, 
  Clock, 
  Target, 
  Activity, 
  CheckCircle, 
  AlertCircle, 
  Calendar, 
  Globe, 
  Zap, 
  Eye, 
  RefreshCw, 
  PieChart, 
  LineChart, 
  Filter, 
  Download, 
  Share2, 
  Star, 
  Award, 
  Gauge, 
  Building,
  MessageSquare,
  ThumbsUp,
  TrendingDown,
  Percent,
  Timer
} from 'lucide-react'

export const AnalyticsDepartamentaisFeatureDetailPage: React.FC = () => {
  const { featureId } = useParams()
  const { t } = useTranslation(['workforce', 'analytics'])

  if (!featureId || featureId !== 'analytics') {
    return <Navigate to="/workforce" replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <a 
            href="/workforce" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
          
          <div className="text-center">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-full mb-8 shadow-lg"
            >
              <BarChart3 className="w-7 h-7" />
              <span className="font-bold text-lg">{t('features.analytics.title')}</span>
            </motion.div>
            <h1 className="text-6xl font-extrabold bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-8">
              {t('features.analytics.title')}
            </h1>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              {t('features.analytics.description')}
            </p>
          </div>
        </motion.div>

        {/* Dashboard Principal */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-24"
        >
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-10 shadow-2xl border border-white/50">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-full mb-6">
                <Activity className="w-6 h-6" />
                <span className="font-semibold">{t('analytics:analytics.mainDashboard.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('analytics:analytics.mainDashboard.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('analytics:analytics.mainDashboard.description')}
              </p>
            </div>

            {/* Mock Dashboard */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <Building className="w-6 h-6 text-blue-600" />
                    {t('analytics:analytics.mainDashboard.dashboardTitle')}
                  </h3>
                  <p className="text-gray-600">{t('analytics:analytics.mainDashboard.dashboardDescription')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    <Calendar className="w-4 h-4" />
                    {t('analytics:analytics.mainDashboard.planningButton')}
                  </button>
                  <button className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium">
                    <BarChart3 className="w-4 h-4" />
                    {t('analytics:analytics.mainDashboard.reportsButton')}
                  </button>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-blue-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-600">{t('analytics:analytics.mainDashboard.stats.activeEmployees.title')}</span>
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">8</div>
                  <div className="text-xs text-gray-500">{t('analytics:analytics.mainDashboard.stats.activeEmployees.description')}</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-green-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-600">{t('analytics:analytics.mainDashboard.stats.onlineEmployees.title')}</span>
                    <Activity className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">6</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    {t('analytics:analytics.mainDashboard.stats.onlineEmployees.description')}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-orange-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-600">{t('analytics:analytics.mainDashboard.stats.schedulesToday.title')}</span>
                    <Clock className="w-5 h-5 text-orange-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">3</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    {t('analytics:analytics.mainDashboard.stats.schedulesToday.description')}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-purple-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-600">{t('analytics:analytics.mainDashboard.stats.productivity.title')}</span>
                    <Target className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">87%</div>
                  <div className="text-xs text-gray-500">{t('analytics:analytics.mainDashboard.stats.productivity.description')}</div>
                </motion.div>
              </div>

              {/* Satisfaction Metric */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white mb-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xl font-bold mb-2">{t('analytics:analytics.mainDashboard.satisfaction.title')}</h4>
                    <p className="text-green-100">{t('analytics:analytics.mainDashboard.satisfaction.subtitle')}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold mb-1">85%</div>
                    <div className="text-green-200 text-sm">{t('analytics:analytics.mainDashboard.satisfaction.increase')}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white h-3 rounded-full transition-all duration-1000" style={{width: '85%'}}></div>
                  </div>
                  <div className="flex justify-between text-xs text-green-100 mt-2">
                    <span>{t('analytics:analytics.mainDashboard.satisfaction.scale.min')}</span>
                    <span className="font-medium">{t('analytics:analytics.mainDashboard.satisfaction.scale.status')}</span>
                    <span>{t('analytics:analytics.mainDashboard.satisfaction.scale.max')}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Métricas Departamentais */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-indigo-100 text-indigo-800 px-6 py-3 rounded-full mb-6">
                <PieChart className="w-6 h-6" />
                <span className="font-semibold">{t('analytics:analytics.departmentalMetrics.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('analytics:analytics.departmentalMetrics.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('analytics:analytics.departmentalMetrics.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Recepção */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{t('analytics:analytics.departmentalMetrics.departments.reception.name')}</h3>
                    <p className="text-sm text-gray-600">{t('analytics:analytics.departmentalMetrics.departments.reception.description')}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{t('analytics:analytics.departmentalMetrics.departments.reception.satisfaction')}</span>
                    <span className="text-lg font-bold text-blue-600">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '85%'}}></div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">6</div>
                      <div className="text-xs text-gray-600">{t('analytics:analytics.departmentalMetrics.metrics.online')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">3</div>
                      <div className="text-xs text-gray-600">{t('analytics:analytics.departmentalMetrics.metrics.schedules')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">87%</div>
                      <div className="text-xs text-gray-600">{t('analytics:analytics.departmentalMetrics.metrics.tasks')}</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Governança */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{t('analytics:analytics.departmentalMetrics.departments.governance.name')}</h3>
                    <p className="text-sm text-gray-600">{t('analytics:analytics.departmentalMetrics.departments.governance.description')}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{t('analytics:analytics.departmentalMetrics.departments.governance.satisfaction')}</span>
                    <span className="text-lg font-bold text-green-600">92%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '92%'}}></div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">12</div>
                      <div className="text-xs text-gray-600">{t('analytics:analytics.departmentalMetrics.metrics.online')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">4</div>
                      <div className="text-xs text-gray-600">{t('analytics:analytics.departmentalMetrics.metrics.schedules')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">94%</div>
                      <div className="text-xs text-gray-600">{t('analytics:analytics.departmentalMetrics.metrics.tasks')}</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* F&B */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{t('analytics:analytics.departmentalMetrics.departments.fb.name')}</h3>
                    <p className="text-sm text-gray-600">{t('analytics:analytics.departmentalMetrics.departments.fb.description')}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{t('analytics:analytics.departmentalMetrics.departments.fb.satisfaction')}</span>
                    <span className="text-lg font-bold text-orange-600">78%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-orange-500 h-2 rounded-full" style={{width: '78%'}}></div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">8</div>
                      <div className="text-xs text-gray-600">Online</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">2</div>
                      <div className="text-xs text-gray-600">Escalas</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">73%</div>
                      <div className="text-xs text-gray-600">Tarefas</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Sistema de Satisfação */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                <Gauge className="w-6 h-6" />
                <span className="font-semibold">{t('analytics:analytics.satisfactionSystem.title')}</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                {t('analytics:analytics.satisfactionSystem.subtitle')}
              </h2>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                {t('analytics:analytics.satisfactionSystem.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Star className="w-7 h-7" />
                    {t('analytics:analytics.satisfactionSystem.interactiveRating.title')}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                        <Gauge className="w-3 h-3 text-green-900" />
                      </div>
                      <span>{t('analytics:analytics.satisfactionSystem.interactiveRating.features.0')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center">
                        <RefreshCw className="w-3 h-3 text-blue-900" />
                      </div>
                      <span>{t('analytics:analytics.satisfactionSystem.interactiveRating.features.1')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-3 h-3 text-purple-900" />
                      </div>
                      <span>{t('analytics:analytics.satisfactionSystem.interactiveRating.features.2')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-pink-400 rounded-full flex items-center justify-center">
                        <Award className="w-3 h-3 text-pink-900" />
                      </div>
                      <span>{t('analytics:analytics.satisfactionSystem.interactiveRating.features.3')}</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Eye className="w-7 h-7" />
                    {t('analytics:analytics.satisfactionSystem.automaticInsights.title')}
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-white/10 rounded-xl p-3">
                      <div className="font-semibold mb-1">{t('analytics:analytics.satisfactionSystem.automaticInsights.strengths.title')}</div>
                      <div className="text-sm text-green-200">{t('analytics:analytics.satisfactionSystem.automaticInsights.strengths.description')}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                      <div className="font-semibold mb-1">{t('analytics:analytics.satisfactionSystem.automaticInsights.opportunities.title')}</div>
                      <div className="text-sm text-orange-200">{t('analytics:analytics.satisfactionSystem.automaticInsights.opportunities.description')}</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Mock Satisfaction Interface */}
              <div className="relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2 }}
                  className="bg-white rounded-3xl p-8 shadow-2xl"
                >
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('analytics:analytics.satisfactionSystem.satisfactionQuery')}</h3>
                    <p className="text-gray-600">{t('analytics:analytics.satisfactionSystem.sliderDescription')}</p>
                  </div>

                  <div className="mb-8">
                    <div className="relative">
                      <div className="w-full h-4 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200 rounded-full">
                        <div className="absolute top-1/2 transform -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-lg" style={{left: '85%', marginLeft: '-12px'}}></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>1</span>
                        <span>50</span>
                        <span>100</span>
                      </div>
                    </div>
                    <div className="text-center mt-4">
                      <div className="text-3xl font-bold text-green-600">{t('analytics:analytics.satisfactionSystem.rating.value')}</div>
                      <div className="text-sm text-gray-600">{t('analytics:analytics.satisfactionSystem.rating.description')}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-lg font-bold text-gray-900">{t('analytics:analytics.satisfactionSystem.metrics.today')}</div>
                      <div className="text-2xl font-bold text-green-600">85%</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-lg font-bold text-gray-900">{t('analytics:analytics.satisfactionSystem.metrics.average')}</div>
                      <div className="text-2xl font-bold text-gray-600">77%</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Comunicação Departamental */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-purple-100 text-purple-800 px-6 py-3 rounded-full mb-6">
                <MessageSquare className="w-6 h-6" />
                <span className="font-semibold">{t('analytics:analytics.departmentalCommunication.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('analytics:analytics.departmentalCommunication.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('analytics:analytics.departmentalCommunication.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                  <ThumbsUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('analytics:analytics.departmentalCommunication.types.tips.title')}</h3>
                <p className="text-gray-600 mb-4">{t('analytics:analytics.departmentalCommunication.types.tips.description')}</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-sm font-medium text-gray-900">{t('analytics:analytics.departmentalCommunication.types.tips.stats')}</div>
                    <div className="text-xs text-green-600">{t('analytics:analytics.departmentalCommunication.types.tips.trend')}</div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border border-red-200"
              >
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-4">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('analytics:analytics.departmentalCommunication.types.complaints.title')}</h3>
                <p className="text-gray-600 mb-4">{t('analytics:analytics.departmentalCommunication.types.complaints.description')}</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-sm font-medium text-gray-900">{t('analytics:analytics.departmentalCommunication.types.complaints.stats')}</div>
                    <div className="text-xs text-red-600">{t('analytics:analytics.departmentalCommunication.types.complaints.details')}</div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('analytics:analytics.departmentalCommunication.types.suggestions.title')}</h3>
                <p className="text-gray-600 mb-4">{t('analytics:analytics.departmentalCommunication.types.suggestions.description')}</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-sm font-medium text-gray-900">{t('analytics:analytics.departmentalCommunication.types.suggestions.stats')}</div>
                    <div className="text-xs text-blue-600">{t('analytics:analytics.departmentalCommunication.types.suggestions.details')}</div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Priority Analysis */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {t('analytics:analytics.departmentalCommunication.priorityAnalysis.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <AlertCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-red-600 mb-1">{t('analytics:analytics.departmentalCommunication.priorityAnalysis.levels.high.title')}</div>
                  <div className="text-sm text-gray-600">{t('analytics:analytics.departmentalCommunication.priorityAnalysis.levels.high.description')}</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-yellow-600 mb-1">{t('analytics:analytics.departmentalCommunication.priorityAnalysis.levels.medium.title')}</div>
                  <div className="text-sm text-gray-600">{t('analytics:analytics.departmentalCommunication.priorityAnalysis.levels.medium.description')}</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-green-600 mb-1">{t('analytics:analytics.departmentalCommunication.priorityAnalysis.levels.low.title')}</div>
                  <div className="text-sm text-gray-600">{t('analytics:analytics.departmentalCommunication.priorityAnalysis.levels.low.description')}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Posts Departamentais */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                <Share2 className="w-6 h-6" />
                <span className="font-semibold">{t('analytics:analytics.departmentalPosts.title')}</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                {t('analytics:analytics.departmentalPosts.subtitle')}
              </h2>
              <p className="text-xl text-purple-100 max-w-3xl mx-auto">
                {t('analytics:analytics.departmentalPosts.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <h3 className="text-xl font-bold mb-4">{t('analytics:analytics.departmentalPosts.engagement.title')}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold mb-1">45</div>
                      <div className="text-sm text-purple-200">{t('analytics:analytics.departmentalPosts.engagement.metrics.posts')}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold mb-1">168</div>
                      <div className="text-sm text-purple-200">{t('analytics:analytics.departmentalPosts.engagement.metrics.likes')}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold mb-1">89</div>
                      <div className="text-sm text-purple-200">{t('analytics:analytics.departmentalPosts.engagement.metrics.comments')}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold mb-1">23</div>
                      <div className="text-sm text-purple-200">{t('analytics:analytics.departmentalPosts.engagement.metrics.shares')}</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <h3 className="text-xl font-bold mb-4">{t('analytics:analytics.departmentalPosts.smartFilters.title')}</h3>
                  <ul className="space-y-2 text-purple-100">
                    <li>• {t('analytics:analytics.departmentalPosts.smartFilters.features.0')}</li>
                    <li>• {t('analytics:analytics.departmentalPosts.smartFilters.features.1')}</li>
                    <li>• {t('analytics:analytics.departmentalPosts.smartFilters.features.2')}</li>
                    <li>• {t('analytics:analytics.departmentalPosts.smartFilters.features.3')}</li>
                  </ul>
                </motion.div>
              </div>

              {/* Mock Posts Panel */}
              <div className="relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.7 }}
                  className="bg-white rounded-3xl p-6 shadow-2xl"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900">{t('analytics:analytics.departmentalPosts.feed.title')}</h3>
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-400" />
                      <Download className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">MS</div>
                        <div>
                          <div className="font-semibold text-gray-900">{t('analytics:analytics.departmentalPosts.feed.post1.author')}</div>
                          <div className="text-xs text-gray-500">{t('analytics:analytics.departmentalPosts.feed.post1.time')} • {t('analytics:analytics.departmentalPosts.feed.post1.department')}</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 mb-3">
                        {t('analytics:analytics.departmentalPosts.feed.post1.content')}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>❤️ {t('analytics:analytics.departmentalPosts.feed.post1.likes')}</span>
                        <span>💬 {t('analytics:analytics.departmentalPosts.feed.post1.comments')}</span>
                        <span>📤 {t('analytics:analytics.departmentalPosts.feed.post1.shares')}</span>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">JS</div>
                        <div>
                          <div className="font-semibold text-gray-900">{t('analytics:analytics.departmentalPosts.feed.post2.author')}</div>
                          <div className="text-xs text-gray-500">{t('analytics:analytics.departmentalPosts.feed.post2.time')} • {t('analytics:analytics.departmentalPosts.feed.post2.department')}</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 mb-3">
                        {t('analytics:analytics.departmentalPosts.feed.post2.content')}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>❤️ {t('analytics:analytics.departmentalPosts.feed.post2.likes')}</span>
                        <span>💬 {t('analytics:analytics.departmentalPosts.feed.post2.comments')}</span>
                        <span>📤 {t('analytics:analytics.departmentalPosts.feed.post2.shares')}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Relatórios e Analytics */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-cyan-100 text-cyan-800 px-6 py-3 rounded-full mb-6">
                <LineChart className="w-6 h-6" />
                <span className="font-semibold">{t('analytics:analytics.reportsAndAnalytics.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('analytics:analytics.reportsAndAnalytics.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('analytics:analytics.reportsAndAnalytics.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200 text-center"
              >
                <TrendingUp className="w-10 h-10 text-cyan-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">{t('analytics:analytics.reportsAndAnalytics.metrics.productivity.title')}</div>
                <div className="text-sm text-gray-600">{t('analytics:analytics.reportsAndAnalytics.metrics.productivity.label')}</div>
                <div className="text-xs text-cyan-600 mt-1">{t('analytics:analytics.reportsAndAnalytics.metrics.productivity.description')}</div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 text-center"
              >
                <Timer className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">{t('analytics:analytics.reportsAndAnalytics.metrics.averageTime.title')}</div>
                <div className="text-sm text-gray-600">{t('analytics:analytics.reportsAndAnalytics.metrics.averageTime.label')}</div>
                <div className="text-xs text-green-600 mt-1">{t('analytics:analytics.reportsAndAnalytics.metrics.averageTime.description')}</div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200 text-center"
              >
                <Percent className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">{t('analytics:analytics.reportsAndAnalytics.metrics.successRate.title')}</div>
                <div className="text-sm text-gray-600">{t('analytics:analytics.reportsAndAnalytics.metrics.successRate.label')}</div>
                <div className="text-xs text-purple-600 mt-1">{t('analytics:analytics.reportsAndAnalytics.metrics.successRate.description')}</div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200 text-center"
              >
                <Award className="w-10 h-10 text-orange-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">{t('analytics:analytics.reportsAndAnalytics.metrics.satisfaction.title')}</div>
                <div className="text-sm text-gray-600">{t('analytics:analytics.reportsAndAnalytics.metrics.satisfaction.label')}</div>
                <div className="text-xs text-orange-600 mt-1">{t('analytics:analytics.reportsAndAnalytics.metrics.satisfaction.description')}</div>
              </motion.div>
            </div>

            {/* Comparison Chart */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                {t('analytics:analytics.reportsAndAnalytics.departmentalComparison.title')}
              </h3>
              <div className="space-y-4">
                {[
                  { deptKey: 'governance', score: 92, color: 'green', trend: 'up' },
                  { deptKey: 'reception', score: 85, color: 'blue', trend: 'up' },
                  { deptKey: 'maintenance', score: 81, color: 'purple', trend: 'stable' },
                  { deptKey: 'fnb', score: 78, color: 'orange', trend: 'down' }
                ].map((dept, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.0 + index * 0.1 }}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl"
                  >
                    <div className={`w-8 h-8 bg-${dept.color}-500 rounded-lg`}></div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{t(`analytics:analytics.reportsAndAnalytics.departmentalComparison.departments.${dept.deptKey}.name`)}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div className={`bg-${dept.color}-500 h-2 rounded-full transition-all duration-1000`} style={{width: `${dept.score}%`}}></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">{dept.score}%</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        {dept.trend === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
                        {dept.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
                        {dept.trend === 'stable' && <div className="w-3 h-3 bg-gray-400 rounded-full"></div>}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Especificações Técnicas */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-br from-slate-900 via-gray-900 to-zinc-900 rounded-3xl p-10 shadow-2xl text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                  <Zap className="w-6 h-6" />
                  <span className="font-semibold">{t('analytics:analytics.technicalSpecs.title')}</span>
                </div>
                <h2 className="text-4xl font-bold mb-6">
                  {t('analytics:analytics.technicalSpecs.subtitle')}
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  {t('analytics:analytics.technicalSpecs.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <BarChart3 className="w-8 h-8 mb-4 text-cyan-400" />
                  <h3 className="text-lg font-bold mb-3">{t('analytics:analytics.technicalSpecs.components.dashboardEngine.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• {t('analytics:analytics.technicalSpecs.components.dashboardEngine.features.0')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.dashboardEngine.features.1')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.dashboardEngine.features.2')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.dashboardEngine.features.3')}</li>
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Target className="w-8 h-8 mb-4 text-green-400" />
                  <h3 className="text-lg font-bold mb-3">{t('analytics:analytics.technicalSpecs.components.satisfactionSystem.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• {t('analytics:analytics.technicalSpecs.components.satisfactionSystem.features.0')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.satisfactionSystem.features.1')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.satisfactionSystem.features.2')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.satisfactionSystem.features.3')}</li>
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Globe className="w-8 h-8 mb-4 text-purple-400" />
                  <h3 className="text-lg font-bold mb-3">{t('analytics:analytics.technicalSpecs.components.communicationAnalytics.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• {t('analytics:analytics.technicalSpecs.components.communicationAnalytics.features.0')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.communicationAnalytics.features.1')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.communicationAnalytics.features.2')}</li>
                    <li>• {t('analytics:analytics.technicalSpecs.components.communicationAnalytics.features.3')}</li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

      </div>
    </div>
  )
}

export default AnalyticsDepartamentaisFeatureDetailPage